/**
 * The stage: everything being shared in the voice channel.
 *
 * Rebuilt after review, closer to how Discord does it:
 *
 * - Everyone can share at once, and every share is a tile. Two or more tile
 *   in a grid; click one to spotlight it, with the others as a strip of
 *   thumbnails underneath that you click to swap in.
 * - The stage is resizable: drag the handle along its bottom edge. The height
 *   is remembered.
 * - Tiles fade and scale in when a share starts, rather than popping.
 * - Each tile can go full screen or pop out into a floating picture-in-picture
 *   window that stays on top while you do something else.
 * - Your own share shows its quality, which you can change without stopping.
 *
 * A MediaStream cannot be handed to React as a prop the way a src can - it has
 * to be attached to the element imperatively, which is what StreamVideo does.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import { useVoice } from '@/stores/voice';
import { useSession } from '@/stores/session';
import { Avatar } from './Avatar';
import type { PublicUser } from '@/lib/types';

type Share = { userId: string; stream: MediaStream; local: boolean; name?: string };

const HEIGHT_KEY = 'pogu.stage.height';
const MIN_H = 180;

function readHeight(): number {
  try {
    const n = Number(localStorage.getItem(HEIGHT_KEY));
    return Number.isFinite(n) && n >= MIN_H ? n : 420;
  } catch { return 420; }
}

export function ScreenStage() {
  const screens = useVoice((s) => s.screens);
  const [spot, setSpot] = useState<string | null>(null);
  const [full, setFull] = useState<string | null>(null);
  const [height, setHeight] = useState(readHeight);

  useEffect(() => {
    if (spot && !screens.some((s) => s.userId === spot)) setSpot(null);
    if (full && !screens.some((s) => s.userId === full)) setFull(null);
  }, [screens, spot, full]);

  if (!screens.length) return null;

  const spotlight = spot ? screens.find((s) => s.userId === spot) ?? null : null;
  const others = spotlight ? screens.filter((s) => s.userId !== spotlight.userId) : [];
  const single = screens.length === 1;
  const fullShare = full ? screens.find((s) => s.userId === full) ?? null : null;

  /* Drag the bottom edge to resize. */
  function startResize(e: React.PointerEvent) {
    e.preventDefault();
    const startY = e.clientY;
    const startH = height;
    const maxH = Math.max(MIN_H, window.innerHeight - 220);
    const move = (ev: PointerEvent) => setHeight(Math.min(maxH, Math.max(MIN_H, startH + ev.clientY - startY)));
    const up = (ev: PointerEvent) => {
      const final = Math.min(maxH, Math.max(MIN_H, startH + ev.clientY - startY));
      try { localStorage.setItem(HEIGHT_KEY, String(Math.round(final))); } catch { /* storage off */ }
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  return (
    <section
      aria-label={single ? 'Shared screen' : screens.length + ' shared screens'}
      className="stage-in relative flex flex-none flex-col border-b border-line-soft bg-[#0b0c0f]"
    >
      <div className="flex items-center gap-2 px-3 pt-2.5 pb-2">
        <span className="relative flex h-2 w-2 flex-none">
          <span className="absolute inset-0 animate-ping rounded-full bg-bad opacity-70 motion-reduce:animate-none" />
          <span className="relative h-2 w-2 rounded-full bg-bad" />
        </span>
        <span className="text-[13px] font-semibold text-white/85">
          {single ? 'Live screen' : screens.length + ' live screens'}
        </span>
        <span className="flex-1" />
        {spotlight && !single ? (
          <StageButton onClick={() => setSpot(null)} label="Show all screens">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="3" width="8" height="8" rx="1.5" /><rect x="13" y="3" width="8" height="8" rx="1.5" /><rect x="3" y="13" width="8" height="8" rx="1.5" /><rect x="13" y="13" width="8" height="8" rx="1.5" /></svg>
            Grid
          </StageButton>
        ) : null}
      </div>

      <div className="min-h-0 px-3" style={{ height }}>
        {spotlight ? (
          <div className="flex h-full flex-col gap-2">
            <Tile share={spotlight} big onClick={() => setSpot(null)} onFull={() => setFull(spotlight.userId)} />
            {others.length ? (
              <div className="flex h-[92px] flex-none gap-2 overflow-x-auto pb-1">
                {others.map((s) => (
                  <div key={s.userId} className="h-full w-[164px] flex-none">
                    <Tile share={s} thumb onClick={() => setSpot(s.userId)} onFull={() => setFull(s.userId)} />
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <div
            className="grid h-full gap-2"
            style={{
              gridTemplateColumns: 'repeat(' + (screens.length === 1 ? 1 : screens.length <= 4 ? 2 : 3) + ', minmax(0, 1fr))',
              gridAutoRows: 'minmax(0, 1fr)'
            }}
          >
            {screens.map((s) => (
              <Tile key={s.userId} share={s} big={single} onClick={() => (single ? setFull(s.userId) : setSpot(s.userId))} onFull={() => setFull(s.userId)} />
            ))}
          </div>
        )}
      </div>

      {/* The resize handle. */}
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize the screen stage"
        tabIndex={0}
        onPointerDown={startResize}
        onKeyDown={(e) => {
          if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
          e.preventDefault();
          setHeight((h) => {
            const next = Math.max(MIN_H, h + (e.key === 'ArrowDown' ? 40 : -40));
            try { localStorage.setItem(HEIGHT_KEY, String(next)); } catch { /* storage off */ }
            return next;
          });
        }}
        className="group flex h-4 cursor-row-resize items-center justify-center outline-none"
      >
        <span className="h-1 w-12 rounded-full bg-white/20 transition-colors duration-[var(--t-hover)] group-hover:bg-white/50 group-focus-visible:bg-accent" />
      </div>

      {fullShare ? <Fullscreen share={fullShare} onClose={() => setFull(null)} /> : null}

      <style>{`
        @keyframes stage-in { from { opacity: 0; transform: translateY(-6px) } to { opacity: 1; transform: none } }
        @keyframes tile-in { from { opacity: 0; transform: scale(.97) } to { opacity: 1; transform: none } }
        .stage-in { animation: stage-in 260ms var(--ease-out, ease-out) both }
        .tile-in { animation: tile-in 280ms var(--ease-out, ease-out) both }
        @media (prefers-reduced-motion: reduce) { .stage-in, .tile-in { animation: none } }
      `}</style>
    </section>
  );
}

function useWho(share: Share): { who: PublicUser | null | undefined; label: string } {
  const members = useVoice((s) => s.members);
  const me = useSession((s) => s.me);
  const who = share.local ? me : members.find((m) => m.userId === share.userId)?.user;
  const label = share.local ? 'Your screen' + (share.name ? ' · ' + share.name : '') : (who?.displayName ?? 'Someone') + "'s screen";
  return { who, label };
}

function StreamVideo({ stream, fit, className }: { stream: MediaStream; fit: 'contain' | 'cover'; className?: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    if (video.srcObject !== stream) video.srcObject = stream;
    void video.play().catch(() => {});
    return () => { video.srcObject = null; };
  }, [stream]);

  return (
    <video
      ref={ref}
      muted
      playsInline
      autoPlay
      onLoadedData={() => setReady(true)}
      className={cn(
        'h-full w-full bg-black transition-opacity duration-300 ease-out',
        fit === 'contain' ? 'object-contain' : 'object-cover',
        ready ? 'opacity-100' : 'opacity-0',
        className
      )}
    />
  );
}

function Tile({ share, big, thumb, onClick, onFull }: {
  share: Share;
  big?: boolean;
  thumb?: boolean;
  onClick: () => void;
  onFull: () => void;
}) {
  const { who, label } = useWho(share);
  const wrap = useRef<HTMLDivElement>(null);

  async function popOut() {
    const video = wrap.current?.querySelector('video') as (HTMLVideoElement & { requestPictureInPicture?: () => Promise<unknown> }) | null;
    try { await video?.requestPictureInPicture?.(); } catch { /* not allowed here */ }
  }

  const pipSupported = typeof document !== 'undefined' && 'pictureInPictureEnabled' in document && (document as Document & { pictureInPictureEnabled?: boolean }).pictureInPictureEnabled;

  return (
    <div
      ref={wrap}
      className={cn(
        'tile-in group relative h-full min-h-0 overflow-hidden rounded-[calc(12px*var(--radius-mul,1))] bg-black ring-1 ring-white/10',
        'transition-[box-shadow] duration-[var(--t-hover)] hover:ring-white/30'
      )}
    >
      <button
        type="button"
        onClick={onClick}
        aria-label={thumb ? 'Spotlight ' + label : big ? label + ', open full screen' : 'Spotlight ' + label}
        className="absolute inset-0 outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <StreamVideo stream={share.stream} fit={thumb ? 'cover' : 'contain'} />
      </button>

      <span className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/80 to-transparent px-2.5 pt-8 pb-2">
        {who ? <Avatar user={who} size={thumb ? 18 : 22} /> : null}
        <span className={cn('truncate font-semibold text-white', thumb ? 'text-[11.5px]' : 'text-[13px]')}>{label}</span>
        {share.local ? <span className="flex-none rounded bg-bad px-1.5 py-px text-[10px] font-bold text-white">LIVE</span> : null}
      </span>

      {!thumb ? (
        <span className="absolute top-2 right-2 flex gap-1.5 opacity-0 transition-opacity duration-[var(--t-hover)] group-focus-within:opacity-100 group-hover:opacity-100">
          {share.local ? <QualityPicker /> : null}
          {pipSupported ? (
            <StageButton onClick={() => void popOut()} label="Pop out">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><rect x="12" y="12" width="7" height="5" rx="1" fill="currentColor" /></svg>
            </StageButton>
          ) : null}
          <StageButton onClick={onFull} label="Full screen">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></svg>
          </StageButton>
        </span>
      ) : null}
    </div>
  );
}

function StageButton({ onClick, label, children }: { onClick: () => void; label: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="relative z-[1] flex h-8 items-center gap-1.5 rounded-[calc(10px*var(--radius-mul,1))] bg-black/55 px-2 text-[12.5px] font-semibold text-white/90 outline-none backdrop-blur-sm transition-colors duration-[var(--t-hover)] hover:bg-black/75 hover:text-white focus-visible:ring-2 focus-visible:ring-accent"
    >
      {children}
    </button>
  );
}

/**
 * Your own share's quality, changed without stopping it.
 *
 * The top step costs bandwidth on everyone else's connection, not only yours,
 * which is why it stays a perk.
 */
function QualityPicker() {
  const voice = useVoice();
  const me = useSession((s) => s.me);
  const premium = me?.tier === 'premium' || me?.tier === 'vip' || me?.role === 'admin' || (me?.level ?? 0) >= 30;

  const options: ['smooth' | 'balanced' | 'sharp', string, string, boolean][] = [
    ['smooth', '1080p60', 'Smooth motion, for games', true],
    ['balanced', '1440p30', 'Sharp text, for code and documents', true],
    ['sharp', '1440p60', 'Sharp and smooth - Premium, VIP or level 30', premium]
  ];

  return (
    <span className="relative z-[1] flex items-center gap-0.5 rounded-[calc(10px*var(--radius-mul,1))] bg-black/55 p-0.5 backdrop-blur-sm" role="group" aria-label="Share quality">
      {options.map(([id, label, note, allowed]) => (
        <button
          key={id}
          type="button"
          disabled={!allowed}
          aria-pressed={voice.shareQuality === id}
          title={note}
          onClick={(e) => { e.stopPropagation(); void voice.setShareQuality(id); }}
          className={cn(
            'h-7 rounded-[calc(8px*var(--radius-mul,1))] px-2 text-[12px] font-semibold transition-colors duration-[var(--t-hover)]',
            !allowed && 'cursor-not-allowed opacity-40',
            voice.shareQuality === id ? 'bg-white text-black' : 'text-white/85 hover:bg-white/15 hover:text-white'
          )}
        >
          {label}
        </button>
      ))}
    </span>
  );
}

/** One share, filling the window. Esc or the button closes it. */
function Fullscreen({ share, onClose }: { share: Share; onClose: () => void }) {
  const voice = useVoice();
  const { who, label } = useWho(share);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={label} className="stage-in fixed inset-0 z-[320] flex flex-col bg-black">
      <StreamVideo stream={share.stream} fit="contain" className="min-h-0 flex-1" />

      <div className="absolute inset-x-0 top-0 flex items-center gap-3 bg-gradient-to-b from-black/80 to-transparent px-4 pt-3 pb-10">
        {who ? <Avatar user={who} size={26} /> : null}
        <span className="truncate text-[14px] font-semibold text-white">{label}</span>
        <span className="flex-1" />
        {share.local ? <QualityPicker /> : null}
        {share.local ? (
          <button
            type="button"
            onClick={() => voice.stopScreenShare()}
            className="flex h-8 items-center gap-2 rounded-full bg-bad/80 px-3.5 text-[13px] font-semibold text-white transition-colors hover:bg-bad"
          >
            Stop sharing
          </button>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          className="flex h-8 items-center gap-2 rounded-full bg-white/15 px-3.5 text-[13px] font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/25"
        >
          Exit full screen
        </button>
      </div>
    </div>,
    document.body
  );
}
